/**
 * ContextSidebar — чёрный rail + светлая контекстная панель (redesign по
 * макету «Главная»): иконки сервисов · язык · CSI/STAT — на rail;
 * выбор КАТО · аким · ключевые показатели — на панели. Панель сворачивается.
 * Отображается на всех страницах (§3 FE-15).
 *
 * Источник КАТО (fallback-цепочка):
 *  1. `:kato` из URL (/:kato/digital-passport) — через matchPath, потому что
 *     useParams в layout-компоненте не видит параметры дочернего роута;
 *  2. `useKato().selectedKato` — выбранный через селектор / клик маркера;
 *  3. 150000000 (дефолтная область).
 *
 * Карточки (аким, ключевые показатели) видны всегда: при отсутствии данных
 * по КАТО остаются только названия, при смене КАТО значения перезапрашиваются.
 * Видимостью управляет супер-администратор («Видимость витрины», US-09
 * FR-1): блок акима — `left-menu.akim`, каждый показатель — свой
 * `left-menu.*`. Выключенное не рендерится; кнопка сворачивания панели
 * остаётся всегда — это управление, а не данные.
 *
 * Планшеты (< `lg`, 1024 px): rail + панель занимали 356 px из 768 и
 * контенту оставалось ~410 px, поэтому панель по умолчанию свёрнута, а
 * раскрытая ложится поверх контента (drawer с затемнением), не сужая его.
 * От `lg` панель снова стоит в потоке, как на десктопе.
 */
import {
  ArrowLeftFromLine,
  ArrowRightFromLine,
  Headset,
  Settings,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';

import { BlockLoader } from '../common/BlockLoader';
import { MODULES } from '../../consts/modules';
import { useKato } from '../../contexts/KatoContext';
import {
  useAnyModulePresent,
  useModulePresent,
} from '../../hooks/useModulePresent';
import { useYear } from '../../contexts/YearContext';
import akimHard from '../../hardData/akim.json';
import { isHardDataKato } from '../../hardData/constants';
import sideBarHard from '../../hardData/sideBar.json';
import { cn } from '../../lib/cn';
import { katoLevel } from '../../lib/kato-level';
import { getDistrictPopulation } from '../../requests/district.request';
import { getGeneral, getKatoStatistics } from '../../requests/public.request';
import type {
  AkimInfo,
  GeneralInfo,
  PublicGeneralResponse,
} from '../../types/public';
import { KatoSelector } from './KatoSelector';
import { Akim } from './sidebar/Akim';
import { Indicators, type IndicatorTrends } from './sidebar/Indicators';

const FALLBACK_KATO = 150000000;

/** С этой ширины панель стоит в потоке и раскрыта по умолчанию (Tailwind `lg`). */
const IN_FLOW_WIDTH = 1024;

/** Экран уже планшетного порога — панель по умолчанию свёрнута до rail. */
const isNarrowViewport = (): boolean =>
  typeof window !== 'undefined' && window.innerWidth < IN_FLOW_WIDTH;

/** Мок сайдбара (hardData/sideBar.json) → форма GeneralInfo + тренды. */
const HARD_GENERAL: GeneralInfo = {
  population: sideBarHard.indicators.population.value,
  village_count: sideBarHard.indicators.village_count.value,
  saldo_migration: sideBarHard.indicators.saldo_migration.value,
  avg_salary: sideBarHard.indicators.avg_salary.value,
  unemployed: sideBarHard.indicators.unemployed.value,
  area_km2: sideBarHard.indicators.area_km2.value,
  year: sideBarHard.year,
};

const HARD_TRENDS: IndicatorTrends = {
  population: sideBarHard.indicators.population.trend,
  village_count: sideBarHard.indicators.village_count.trend,
  saldo_migration: sideBarHard.indicators.saldo_migration.trend,
  avg_salary: sideBarHard.indicators.avg_salary.trend,
  unemployed: sideBarHard.indicators.unemployed.trend,
  area_km2: sideBarHard.indicators.area_km2.trend,
};

const railIcons = [
  // { key: 'profile', icon: UserRound },
  { key: 'settings', icon: Settings },
  { key: 'support', icon: Headset },
] as const;

interface RailProps {
  collapsed: boolean;
  onExpand: () => void;
}

const Rail = ({ collapsed, onExpand }: RailProps) => {
  const { t, i18n } = useTranslation();
  return (
    // z над затемнением drawer'а: язык и иконки rail остаются нажимаемыми,
    // пока панель открыта поверх контента (планшеты).
    <div className="relative z-[1000] flex w-14 shrink-0 flex-col items-center bg-[#0F1512] pb-4 pt-3">
      <div className="flex flex-col items-center gap-2">
        {railIcons.map(({ key, icon: Icon }) => (
          <button
            key={key}
            type="button"
            disabled
            aria-disabled="true"
            aria-label={t(`rail.${key}`)}
            className="flex size-9 cursor-not-allowed items-center justify-center rounded-lg border border-white/10 text-white/40"
          >
            <Icon className="size-4" />
          </button>
        ))}
        {collapsed && (
          <button
            type="button"
            onClick={onExpand}
            aria-label={t('sidebar.expand')}
            className="flex size-9 items-center justify-center rounded-lg border border-white/10 text-brand-500 transition-colors hover:bg-white/10"
          >
            <ArrowRightFromLine className="size-4" />
          </button>
        )}
      </div>

      <div className="mt-auto flex flex-col items-center gap-2">
        {(['kz', 'ru'] as const).map((lng) => (
          <button
            key={lng}
            type="button"
            onClick={() => i18n.changeLanguage(lng)}
            className={cn(
              'flex size-8 items-center justify-center rounded-lg text-[11px] font-bold transition-colors',
              i18n.resolvedLanguage === lng
                ? 'bg-white text-[#0F1512]'
                : 'text-white/45 hover:text-white',
            )}
          >
            {lng.toUpperCase()}
          </button>
        ))}
        {/* <img
          src={sdLogo}
          alt="SmartDerek"
          className="mt-2 h-6 w-[22px]"
          data-testid="rail-sd-logo"
        /> */}
      </div>
    </div>
  );
};

export const ContextSidebar = () => {
  const { pathname } = useLocation();
  const urlKato = matchPath('/:kato/digital-passport', pathname)?.params.kato;
  const { selectedKato } = useKato();
  const katoNum = urlKato ? Number(urlKato) : (selectedKato ?? FALLBACK_KATO);
  const { selectedYear } = useYear();
  const { t, i18n } = useTranslation();

  const [collapsed, setCollapsed] = useState(isNarrowViewport);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [data, setData] = useState<PublicGeneralResponse | null>(null);
  const [loading, setLoading] = useState(false);
  // village_total по КАТО из /public/kato/statistics — источник
  // «Количества населённых пунктов»: в general_info этого поля нет (⚠️
  // в «Показателях»), village_count приходит null.
  const [villageTotals, setVillageTotals] = useState<Record<
    string,
    number
  > | null>(null);

  // Поворот планшета / изменение окна: реагируем только на ПЕРЕХОД через
  // порог (уже порога — свернуть, шире — раскрыть). Иначе любое изменение
  // размера окна отменяло бы ручной выбор пользователя.
  const wasNarrow = useRef(isNarrowViewport());
  useEffect(() => {
    const onResize = () => {
      const narrow = isNarrowViewport();
      if (narrow === wasNarrow.current) return;
      wasNarrow.current = narrow;
      setCollapsed(narrow);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getKatoStatistics()
      .then((totals) => {
        if (!cancelled) setVillageTotals(totals);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('ContextSidebar getKatoStatistics failed', err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Население области и района — тот же источник и та же отчётная дата,
  // что у блока «Население» паспорта (БНС на 1 число месяца): снимок
  // `general` отставал на месяцы, и цифры панели и блока расходились.
  const [bnsPopulation, setBnsPopulation] = useState<number | null>(null);
  useEffect(() => {
    setBnsPopulation(null);
    const level = Number.isFinite(katoNum) ? katoLevel(katoNum) : null;
    if (
      (level !== 'OBLAST' && level !== 'DISTRICT') ||
      isHardDataKato(katoNum)
    ) {
      return;
    }
    let cancelled = false;
    getDistrictPopulation(katoNum)
      .then((res) => {
        if (!cancelled && res.status === 'OK') setBnsPopulation(res.total);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('ContextSidebar getDistrictPopulation failed', err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [katoNum]);

  // Демо-режим ЗКО: показатели и аким из hardData.
  const isHardKato = isHardDataKato(katoNum);
  const shownGeneral: GeneralInfo | undefined = isHardKato
    ? HARD_GENERAL
    : data?.general && {
        ...data.general,
        population: bnsPopulation ?? data.general.population,
        village_count:
          data.general.village_count ??
          villageTotals?.[String(katoNum)] ??
          null,
        // «Безработные» живут в population_dynamics_details —
        // general.unemployed бэк не заполняет.
        unemployed:
          data.populationDynamicsDetails?.unemployed_population_count ??
          data.general.unemployed,
      };
  const shownTrends = isHardKato ? HARD_TRENDS : undefined;
  const shownAkim: AkimInfo | undefined = isHardKato
    ? akimHard.akim
    : data?.akim;

  // Видимость левого меню — «Видимость витрины» (US-09 FR-1): блок акима
  // и каждый показатель отдельно; заголовок «Ключевые показатели» уходит
  // вместе с последним включённым показателем.
  const showAkim = useModulePresent(MODULES.leftMenu.akim);
  const showIndicators = useAnyModulePresent([
    MODULES.leftMenu.population,
    MODULES.leftMenu.villageCount,
    MODULES.leftMenu.saldoMigration,
    MODULES.leftMenu.avgSalary,
    MODULES.leftMenu.unemployed,
    MODULES.leftMenu.area,
  ]);

  /** Сворачивание панели — управление, а не данные: живёт вне гейтов. */
  const collapseButton = (
    <button
      type="button"
      onClick={() => setCollapsed(true)}
      aria-label={t('sidebar.collapse')}
      className="shrink-0 text-brand-600 transition-colors hover:text-brand-700"
    >
      <ArrowLeftFromLine className="size-[18px]" />
    </button>
  );

  useEffect(() => {
    // Демо-области показывают моки hardData — запрос к бэку не нужен.
    if (!Number.isFinite(katoNum) || isHardDataKato(katoNum)) return;
    let cancelled = false;
    setLoading(true);
    getGeneral(katoNum, selectedYear ?? undefined)
      .then((response) => {
        if (!cancelled) {
          setData(response);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('ContextSidebar getGeneral failed', err);
          // Ошибка запроса → карточки остаются с одними названиями,
          // а не с зависшими значениями прошлого КАТО.
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [katoNum, selectedYear]);

  return (
    <aside className="relative flex shrink-0 text-text">
      <Rail collapsed={collapsed} onExpand={() => setCollapsed(false)} />
      {!collapsed && (
        <>
          {/* Затемнение под drawer'ом: тап по нему сворачивает панель.
              От lg панель в потоке — затемнение не нужно. */}
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => setCollapsed(true)}
            className="fixed inset-0 top-16 z-[900] bg-black/40 lg:hidden"
          />
          <div className="absolute inset-y-0 left-14 z-[1000] flex w-[300px] max-w-[calc(100vw-3.5rem)] flex-col gap-3.5 overflow-y-auto border-r border-surface-border bg-surface-page px-3.5 py-4 shadow-pop lg:static lg:z-auto lg:max-w-none lg:shadow-none">
            <KatoSelector open={selectorOpen} onOpenChange={setSelectorOpen} />

            {!selectorOpen && (
              <>
                {showAkim ? (
                  <section className="rounded-2xl bg-white p-4 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
                    {/* Вместо статичного «Глава района» в шапке — должность
                        акима из данных («Аким района», замечание дизайн-ревью);
                        справа — кнопка сворачивания. */}
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className="min-w-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-faint"
                        data-testid="akim-section-label"
                      >
                        {(i18n.resolvedLanguage === 'kz'
                          ? shownAkim?.position_kz
                          : shownAkim?.position_ru) ?? ''}
                      </span>
                      {collapseButton}
                    </div>
                    {/* Пока грузится /public/general — лоадер вместо акима. */}
                    {loading && !isHardKato ? (
                      <BlockLoader testId="sidebar-akim-loader" />
                    ) : (
                      shownAkim && <Akim akim={shownAkim} />
                    )}
                  </section>
                ) : (
                  // Блок акима выключен супер-админом — карточки нет, но
                  // панель по-прежнему сворачивается.
                  <div className="flex justify-end">{collapseButton}</div>
                )}
                {showIndicators && (
                  <>
                    <div className="mt-1 px-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-faint">
                      {t('sidebar.keyIndicators')}
                    </div>
                    {loading && !isHardKato ? (
                      <BlockLoader testId="sidebar-indicators-loader" />
                    ) : (
                      <Indicators general={shownGeneral} trends={shownTrends} />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}
    </aside>
  );
};
